import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { Accounts } from '@aviata/platform-types';

import { createPassenger, editPassenger } from './api';

type CreatePassengerVariables = {
  userId: string;
  passenger: Partial<Accounts.Passenger>;
};

type EditPassengerVariables = {
  userId: string;
  passenger: Partial<Accounts.Passenger>;
};

export const useCreatePassenger = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['passengerCreatePassenger'],
    mutationFn: ({ userId, passenger }: CreatePassengerVariables) =>
      createPassenger(userId, passenger),
    onSuccess: (_data: Accounts.Passenger) => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });

  const createPassengerWithCallback = (
    variables: CreatePassengerVariables,
    onSuccess?: (data: Accounts.Passenger) => void,
  ) => {
    return mutation.mutateAsync(variables).then(data => {
      onSuccess?.(data);
      return data;
    });
  };

  return {
    ...mutation,
    createPassengerWithCallback,
  };
};

export const useEditPassenger = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['passengerEditPassenger'],
    mutationFn: ({ userId, passenger }: EditPassengerVariables) =>
      editPassenger(userId, passenger),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });

  const editPassengerWithCallbacks = (
    variables: EditPassengerVariables,
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ) => {
    return mutation
      .mutateAsync(variables)
      .then(() => callbacks?.onSuccess?.())
      .catch(() => callbacks?.onError?.());
  };

  return {
    ...mutation,
    editPassengerWithCallbacks,
  };
};
